import { createAsyncThunk } from '@reduxjs/toolkit';

// types
import { ContextMenuItem } from '../menus/context-menu';
import {
  DataItemType,
  DataQuery,
  JobType,
  Site,
  SiteStorageArea,
  StorageArea,
} from '../../utils/types';
import { Event, EventType, EventTarget, MoveDataToStorage } from '../../utils/events';

// types specifically for this component.
import {
  QueryParameters,
  SelectedRowsType,
} from '../search-catalog/search-catalog-types';

// functions
import { APIPrefix, siteName } from '../../utils/functions';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

import { store } from '../../app/store';

// import state definition and functions.
import { DataState, rD_updateRucioLocations } from '../../app/data-slice';
import { rG_setSubmittingJob } from '../../app/global-slice';
import {
  SearchCatalogCAOMState,
  rowCheckedHandler,
  setBuiltQuery,
  setCollections,
  setCollectionDisplayed,
  setCollectionValue,
  setColumns,
  setContextMenuRefreshAddMetadata,
  setContextMenuRefreshStageData,
  setDataProductTypes,
  setDisplayedPage,
  setDPTInResults,
  setDPTRetrieved,
  setErrorMessage,
  setExistingCollection,
  setExistingCollectionText,
  setFacilityDisplayed,
  setFacilityValue,
  setFilenameDisplayed,
  setFilenameValue,
  setInstrumentDisplayed,
  setInstrumentValue,
  setLoadingTapData,
  setNamespaceDisplayed,
  setNamespaceValue,
  setPage,
  setPages,
  setQueries,
  setSelectAll,
  setTapData,
  setTotal,
} from './search-catalog-caom-slice';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_SEARCH_CATALOG_FORM,
  CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS,
  CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM,
  ContextMenuState,
  ctx_setContextMenuDefinition,
} from '../menus/context-menu-slice';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	F U N C T I O N S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Rebuild the context menu on the stage all button.
//
//	------------------------------------------------------------

export function buildContextMenuAddMetadata(args: {
  menuID: CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM;
  parentRowID: number;
}) {
  //
  // store the items so we can return them from the function.
  let items: ContextMenuItem[] = [];

  // update the sites menu.
  if (args.menuID === CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.METADATA_LIST) {
    //
    items = [
      {
        id: 'mnu-metadata-namespace',
        text: 'Namespace',
        childMenu: CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.NONE,
        onClick: true,
        enabled: true,
      },
      {
        id: 'mnu-metadata-filename',
        text: 'Filename',
        childMenu: CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.NONE,
        onClick: true,
        enabled: true,
      },
      {
        id: 'mnu-metadata-collection',
        text: 'Collection',
        childMenu: CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.NONE,
        onClick: true,
        enabled: true,
      },
      {
        id: 'mnu-metadata-facility',
        text: 'Facility',
        childMenu: CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.NONE,
        onClick: true,
        enabled: true,
      },
      {
        id: 'mnu-metadata-instrument',
        text: 'Instrument',
        childMenu: CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.NONE,
        onClick: true,
        enabled: true,
      },
    ];
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM_CAOM,
        contextMenu: CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.METADATA_LIST,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // refresh the menu.
  store.dispatch(setContextMenuRefreshAddMetadata(Date.now()));

  // return the items.
  return items;
  //
} // buildContextMenuAddMetadata

//	------------------------------------------------------------
//
//	build a new context menu when the mouse hovers over the
//	parent menu.
//
//	------------------------------------------------------------

export function buildContextMenuStageData(args: {
  menuID: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS;
  parentRowID: number;
}) {
  //
  const stateFetch: any = store.getState();
  const state: SearchCatalogCAOMState = stateFetch.searchCatalogCAOM;
  const stateData: DataState = stateFetch.data;

  // store the items so we can return them from the function.
  let items: ContextMenuItem[] = [];

  // rebuild the main menu.
  if (args.menuID === CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.MAIN) {
    //
    // define a separator.
    const separator: ContextMenuItem = {
      id: '',
      text: '---',
      childMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.NONE,
    };

    const moveItemTo: ContextMenuItem = {
      id: 'mnu-main-moveitemto',
      text: 'Move item(s) to',
      childMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.SITES,
      onClick: false,
      enabled: state.selectedItems.selectedFiles.length > 0,
    };
    items.push(moveItemTo);

    const moveAllTo: ContextMenuItem = {
      id: 'mnu-main-moveallto',
      text: 'Move all to',
      childMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.SITES,
      onClick: false,
      enabled: state.selectedItems.selectedRows.length > 0,
    };
    items.push(moveAllTo);

    items.push(separator);

    const unselectItem: ContextMenuItem = {
      id: 'mnu-main-unselectitem',
      text: 'Unselect item(s)',
      childMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.NONE,
      onClick: true,
      enabled: state.selectedItems.selectedFiles.length > 0,
    };
    items.push(unselectItem);

    const unselectAll: ContextMenuItem = {
      id: 'mnu-main-unselectall',
      text: 'Unselect all',
      childMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.NONE,
      onClick: true,
      enabled: state.selectedItems.selectedRows.length > 0,
    };
    items.push(unselectAll);

    // update the context menu definition.
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS,
        contextMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.MAIN,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    store.dispatch(setContextMenuRefreshStageData(Date.now()));
    //
  }

  // if we've entered one of the 'Move to' options on the main context menu then
  // rebuild the sites list menu.
  if (args.menuID === CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.SITES) {
    //
    items = stateData.storageAreas.map((item) => ({
      id: 'menu-node-' + (item.site.node ?? '') + '-site-' + (item.site.site ?? ''),
      text: siteName({ site: item.site }),
      childMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.STORAGES,
      onClick: false,
      enabled: true,
    }));
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS,
        contextMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.SITES,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    store.dispatch(setContextMenuRefreshStageData(Date.now()));
    //
  }

  // if we've hovered over a site, then build the storages list menu.
  if (args.menuID === CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.STORAGES) {
    //
    items = stateData.storageAreas[args.parentRowID].storage_areas.map(
      (item: StorageArea) => ({
        id: 'menu-storage-' + item.storage_id,
        text: item.name !== '' ? item.name : '<' + item.relative_path + '>',
        childMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.NONE,
        onClick: true,
        enabled: true,
      }),
    );
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS,
        contextMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.STORAGES,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    store.dispatch(setContextMenuRefreshStageData(Date.now()));
    //
  }

  // return the list of items.
  return items;
  //
} // buildContextMenuStageData

//	-------------------------------------------------
//
//	look through the data-management jobs and build
//	a list of files that are currently in the process
//	of being staged.
//
//	-------------------------------------------------

function _buildMovingLocations(args: { dataManagementJobs: JobType[] }) {
  //
  let locations: {
    namespace: string;
    filename: string;
    refresh: boolean;
    movingSites: Site[];
  }[] = [];

  // loop through the data-management jobs, and the files being transferred.
  for (let i: number = 0; i < args.dataManagementJobs.length; i++) {
    if (args.dataManagementJobs[i].status.toUpperCase() === 'STAGING') {
      for (let j: number = 0; j < args.dataManagementJobs[i].files.length; j++) {
        //
        // check if this file already exists in the array.
        const index: number = locations.findIndex(
          (item) =>
            item.namespace === args.dataManagementJobs[i].files[j].scope &&
            item.filename === args.dataManagementJobs[i].files[j].name,
        );
        if (index === -1) {
          //
          // add a new item.
          let newSite: Site = {
            node: args.dataManagementJobs[i].to_node?.toUpperCase() ?? '',
            site: args.dataManagementJobs[i].to_site?.toUpperCase() ?? '',
            site_uuid: args.dataManagementJobs[i].to_site_uuid,
          };
          locations.push({
            namespace: args.dataManagementJobs[i].files[j].scope,
            filename: args.dataManagementJobs[i].files[j].name,
            refresh: false,
            movingSites: [newSite],
          });
          //
        } else {
          //
          // update existing item.
          if (
            locations[index].movingSites.findIndex(
              (item) => item.site_uuid === args.dataManagementJobs[i].to_site_uuid,
            ) === -1
          ) {
            let newSite: Site = {
              node: args.dataManagementJobs[i].to_node?.toUpperCase() ?? '',
              site: args.dataManagementJobs[i].to_site?.toUpperCase() ?? '',
              site_uuid: args.dataManagementJobs[i].to_site_uuid,
            };
            locations[index].movingSites.push(newSite);
          }
          //
        }
        //
      }
    }
  }

  // update state.
  store.dispatch(
    rD_updateRucioLocations({
      items: locations,
    }),
  );
  //
} // _buildMovingLocations

//	------------------------------------------------------------
//
//	Build the adql query on the back end
//
//	------------------------------------------------------------

export const buildQuery = createAsyncThunk(
  'search_catalog_caom/buildQuery',
  async (args: { query: QueryParameters }, thunkAPI) => {
    //
    let urlCommand: string = APIPrefix() + '/v1/data/get_data_query';

    // Check if we have an ADQL query
    if (args.query.adqlQuery) {
      //
      // Clean and format the query
      const cleanQuery = args.query.adqlQuery.trim();

      // add adql query.
      urlCommand = urlCommand + '?query=' + encodeURIComponent(cleanQuery);
      //
    } else {
      //
      // build the list of IDs as a comma-separated list.
      let requiredObsPublisherDid: string = '';
      if (args.query.obs_publisher_did.length > 0) {
        //
        for (let i = 0; i < args.query.obs_publisher_did.length; i++)
          requiredObsPublisherDid =
            requiredObsPublisherDid + "'" + args.query.obs_publisher_did[i] + "',";

        // remove the last comma.
        requiredObsPublisherDid = requiredObsPublisherDid.slice(0, -1);
        //
      }

      // add ra, dec, and fov.
      urlCommand = urlCommand + '?';
      if (
        args.query.ra !== undefined &&
        args.query.dec !== undefined &&
        args.query.fov !== undefined
      ) {
        urlCommand =
          urlCommand +
          'ra=' +
          args.query.ra +
          '&dec=' +
          args.query.dec +
          '&radius=' +
          args.query.fov +
          '&';
      }
      if (requiredObsPublisherDid !== '') {
        urlCommand =
          urlCommand +
          'obs_publisher_did=' +
          encodeURIComponent(requiredObsPublisherDid) +
          '&';
      }
      if (args.query.dataProductType !== undefined) {
        urlCommand =
          urlCommand +
          'dataproduct_type=' +
          encodeURIComponent(args.query.dataProductType) +
          '&';
      }

      // only filter on source name if this is NOT a position search.
      if (
        args.query.sourceName !== undefined &&
        (args.query.ra === undefined ||
          args.query.dec === undefined ||
          args.query.fov === undefined)
      ) {
        const sourceName: string | null = args.query.sourceName;
        urlCommand =
          urlCommand + 'source_name=' + encodeURIComponent(sourceName ?? '') + '&';
      }
      if (args.query.namespace !== undefined) {
        urlCommand =
          urlCommand + 'namespace=' + encodeURIComponent(args.query.namespace) + '&';
      }
      if (args.query.filename !== undefined) {
        urlCommand =
          urlCommand + 'filename=' + encodeURIComponent(args.query.filename) + '&';
      }
      if (args.query.collection !== undefined) {
        urlCommand =
          urlCommand + 'collection=' + encodeURIComponent(args.query.collection) + '&';
      }
      if (args.query.facility !== undefined) {
        urlCommand =
          urlCommand + 'facility=' + encodeURIComponent(args.query.facility) + '&';
      }
      if (args.query.instrument !== undefined) {
        urlCommand =
          urlCommand + 'instrument=' + encodeURIComponent(args.query.instrument) + '&';
      }
      //
    }

    try {
      //
      const apiResult = await fetch(urlCommand, { credentials: 'include' });
      const returnedJson = await apiResult.json();

      // HTTP 200 means that the query was retrieved OK.
      if (apiResult.status === 200) {
        thunkAPI.dispatch(
          setBuiltQuery({ id: args.query.id, builtQuery: returnedJson.adql }),
        );
      }

      //
    } catch (e) {
      console.error('Error building query:', e);
    }
    //
  },
  //
); // buildQuery

//	------------------------------------------------------------
//
//	Builds a text description of the query being run.
//
//	------------------------------------------------------------

export function buildQueryDescription(args: {
  query: QueryParameters;
  builtQuery?: string;
  longFormat?: boolean;
}) {
  //
  let query: string = '';
  const adqlQuery: boolean =
    args.query.adqlQuery !== undefined && args.query.adqlQuery !== '';
  const positionQuery: boolean =
    args.query.ra !== undefined &&
    args.query.dec !== undefined &&
    args.query.fov !== undefined;

  // add position.
  if (positionQuery === true && adqlQuery === false) {
    query = query + "position: '" + args.query.fov?.toFixed(4) + ' degrees from ';
    if (args.query.sourceName !== undefined) {
      query =
        query +
        args.query.sourceName?.toUpperCase() +
        ' (RA ' +
        args.query.ra?.toFixed(6) +
        ', DEC ' +
        args.query.dec?.toFixed(6) +
        ")', ";
    } else {
      query =
        query +
        'RA ' +
        args.query.ra?.toFixed(6) +
        ', DEC ' +
        args.query.dec?.toFixed(6) +
        "', ";
    }
  }

  // add metadata filters.
  if (
    args.query.sourceName !== undefined &&
    positionQuery === false &&
    adqlQuery === false
  ) {
    query = query + "target name: '" + args.query.sourceName + "', ";
  }
  if (args.query.namespace !== undefined && adqlQuery === false) {
    query = query + "namespace: '" + args.query.namespace + "', ";
  }
  if (args.query.filename !== undefined && adqlQuery === false) {
    query = query + "filename: '" + args.query.filename + "', ";
  }
  if (args.query.collection !== undefined && adqlQuery === false) {
    query = query + "collection: '" + args.query.collection + "', ";
  }
  if (args.query.facility !== undefined && adqlQuery === false) {
    query = query + "facility: '" + args.query.facility + "', ";
  }
  if (args.query.instrument !== undefined && adqlQuery === false) {
    query = query + "instrument: '" + args.query.instrument + "', ";
  }
  if (args.query.dataProductType !== undefined && adqlQuery === false) {
    query = query + "data-product type: '" + args.query.dataProductType + "', ";
  }

  // add adql query.
  if (adqlQuery === true && args.longFormat !== true && args.builtQuery !== undefined) {
    query = query + 'ADQL query: ' + args.builtQuery;
  }
  if (adqlQuery === true && args.longFormat !== true && args.builtQuery === undefined) {
    query = query + 'ADQL query: running.....';
  }

  // remove trailing comma and space.
  if (query.slice(-2) === ', ') {
    query = query.slice(0, -2);
  }

  // return something.
  return query;
  //
} // buildQueryDescription

//	------------------------------------------------------------
//
//	check if any data-management jobs have been updated since
//	the files they contain were last location checked. if so,
//	we need to set the refresh flag so the location is checked
//	again.
//
//	------------------------------------------------------------

function _checkIfRefreshNeeded(args: { dataManagementJobs: JobType[] }) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  // store the required updates in a new array.
  let updatedLocationsChanges: {
    namespace: string;
    filename: string;
    refresh: boolean | null;
  }[] = stateData.rucioLocations
    .filter(
      (rucioItem) =>
        //
        // find if this file appears anywhere in the data-management jobs list.
        args.dataManagementJobs.find(
          (dmJob) =>
            //
            dmJob.files.find(
              (file) =>
                file.scope === rucioItem.namespace &&
                file.name === rucioItem.filename &&
                (dmJob.updated_at > (rucioItem.lastChecked ?? '') ||
                  rucioItem.lastChecked === undefined),
            ) !== undefined,
          //
        ) !== undefined,
      //
    )
    .map((rucioItem) => {
      return {
        namespace: rucioItem.namespace,
        filename: rucioItem.filename,
        refresh: true,
      };
    });

  // update the locations state so that all the refresh flags are false.
  if (updatedLocationsChanges.length > 0) {
    store.dispatch(
      rD_updateRucioLocations({
        items: updatedLocationsChanges,
      }),
    );
  }
  //
} // _checkIfRefreshNeeded

//	------------------------------------------------------------
//
//	check if all the rows are selected, and update the select
//	all state.
//
//	------------------------------------------------------------

export function checkSelectAll(args: {
  sourceList: any[];
  selectedRows: SelectedRowsType[];
  columns: string[];
}) {
  //
  let allSelected: boolean = true;
  for (let i: number = 0; i < args.sourceList.length; i++)
    if (
      args.selectedRows.findIndex(
        (element) =>
          element.obsPublisherDid ===
          getFieldValue({ fieldName: 'obs_publisher_did', item: args.sourceList[i] }),
      ) === -1
    ) {
      allSelected = false;
    }

  // return something.
  return allSelected;
  //
} // checkSelectAll

//	------------------------------------------------------------
//
//	load the list of data-product types from the back end.
//
//	------------------------------------------------------------

export const getDataProductTypesInResults = createAsyncThunk(
  'search_catalog_caom/getDataProductTypesInResults',
  async (args: { query: QueryParameters }, thunkAPI) => {
    //
    // fetch the state.
    const stateFetch: any = thunkAPI.getState();
    const state: SearchCatalogCAOMState = stateFetch.searchCatalogCAOM;

    // get state variables.
    const sPageSize: number =
      state.pageSize.find((find) => find.id === args.query.id)?.pageSize ?? 10;

    try {
      //
      // build the list of IDs as a comma-separated list.
      let requiredObsPublisherDid: string = '';
      if (args.query.obs_publisher_did.length > 0) {
        for (let i = 0; i < args.query.obs_publisher_did.length; i++)
          requiredObsPublisherDid =
            requiredObsPublisherDid + "'" + args.query.obs_publisher_did[i] + "',";

        // remove the last comma.
        requiredObsPublisherDid = requiredObsPublisherDid.slice(0, -1);
      }

      let params: string = '?';
      if (
        args.query.ra !== undefined &&
        args.query.dec !== undefined &&
        args.query.fov !== undefined
      ) {
        params =
          params +
          'ra=' +
          args.query.ra.toString() +
          '&dec=' +
          args.query.dec.toString() +
          '&radius=' +
          args.query.fov.toString() +
          '&';
      }
      if (requiredObsPublisherDid !== '')
        params =
          params +
          'obs_publisher_did=' +
          encodeURIComponent(requiredObsPublisherDid) +
          '&';
      if (
        args.query.dataProductType !== undefined &&
        args.query.dataProductType !== '<all>'
      ) {
        params =
          params +
          'data_product_type=' +
          encodeURIComponent(
            args.query.dataProductType === '' ? '<empty>' : args.query.dataProductType,
          ) +
          '&';
      }
      if (args.query.adqlQuery !== undefined) {
        params = params + '&adql_query=' + encodeURIComponent(args.query.adqlQuery) + '&';
      }
      if (args.query.sourceName !== undefined) {
        params =
          params + 'source_name=' + encodeURIComponent(args.query.sourceName ?? '') + '&';
      }
      if (args.query.namespace !== undefined) {
        params = params + 'namespace=' + encodeURIComponent(args.query.namespace) + '&';
      }
      if (args.query.filename !== undefined) {
        params = params + 'filename=' + encodeURIComponent(args.query.filename) + '&';
      }
      if (args.query.collection !== undefined) {
        params = params + 'collection=' + encodeURIComponent(args.query.collection) + '&';
      }
      if (args.query.facility !== undefined) {
        params = params + 'facility=' + encodeURIComponent(args.query.facility) + '&';
      }
      if (args.query.instrument !== undefined) {
        params = params + 'instrument=' + encodeURIComponent(args.query.instrument) + '&';
      }

      // strip any trailing '&' or '?'.
      params = params.slice(0, -1);

      const apiResult = await fetch(APIPrefix() + '/v1/data/get_product_types' + params, {
        credentials: 'include',
      });

      // status is 200 if we found the source, 400 if it wasn't found, or 500 if the query wasn't well formed.
      if (apiResult.status === 200) {
        //
        const returnedJson = await apiResult.json();

        // get the list of data-product types, with the number of records in each one.
        let returnedValues: { dataproduct_type: string; num_records: number }[] =
          returnedJson.result;

        // did we find any records?
        if (returnedValues.length > 0) {
          //
          // Check if we only have one data product type
          if (returnedValues.length === 1) {
            //
            // Only one type - we will add <all> to the end of the list, but
            // the user will not be able to select it because the one type
            // that was returned will be shown instead.
            let newDPT: string[] = [returnedValues[0].dataproduct_type];
            let page: number = 0;
            let total: { dpt: string; total: number }[] = [
              {
                dpt: returnedValues[0].dataproduct_type,
                total: returnedValues[0].num_records,
              },
              {
                dpt: '<all>',
                total: returnedValues[0].num_records,
              },
            ];
            let pages: number = Math.ceil(returnedValues[0].num_records / sPageSize);

            thunkAPI.dispatch(setDPTInResults({ id: args.query.id, dpt: newDPT }));
            thunkAPI.dispatch(setPage({ id: args.query.id, page: page }));
            thunkAPI.dispatch(setPages({ id: args.query.id, pages: pages }));
            thunkAPI.dispatch(setTotal({ id: args.query.id, total: total }));
            thunkAPI.dispatch(setDPTRetrieved({ id: args.query.id, dptRetrieved: true }));
            //
          } else {
            //
            // Multiple data product types - show with <all> option
            let dpt: string[] = [];
            const total: { dpt: string; total: number }[] = [{ dpt: '<all>', total: 0 }];

            // calculate total across all data product types for "All" option
            let totalAllResults = 0;

            // loop through the returned values.
            for (let i = 0; i < returnedValues.length; i++) {
              dpt.push(returnedValues[i].dataproduct_type);
              total.push({
                dpt: returnedValues[i].dataproduct_type,
                total: returnedValues[i].num_records,
              });
              totalAllResults += returnedValues[i].num_records;
            }
            const pages: number = Math.ceil(totalAllResults / sPageSize);

            // update total for all.
            total[0].total = totalAllResults;

            // set the data-product types array to the returned list, with "All" prepended
            let newDPT: string[] = [
              '<all>', // "All" option
              ...dpt,
            ];
            thunkAPI.dispatch(setDPTInResults({ id: args.query.id, dpt: newDPT }));

            thunkAPI.dispatch(setPage({ id: args.query.id, page: 0 }));
            thunkAPI.dispatch(setPages({ id: args.query.id, pages: pages }));
            thunkAPI.dispatch(setTotal({ id: args.query.id, total: total }));

            // set a flag to confirm that we've collected the data-product types.
            thunkAPI.dispatch(setDPTRetrieved({ id: args.query.id, dptRetrieved: true }));
            //
          }
        } else {
          //
          // generate arrays with "All" option and default "other" type.
          let newDPT: string[] = ['<all>'];

          // set the data-product types array to the returned list.
          thunkAPI.dispatch(setDPTInResults({ id: args.query.id, dpt: newDPT }));

          thunkAPI.dispatch(setPage({ id: args.query.id, page: 0 }));
          thunkAPI.dispatch(setPages({ id: args.query.id, pages: 1 }));
          thunkAPI.dispatch(
            setTotal({ id: args.query.id, total: [{ dpt: '<all>', total: 0 }] }),
          );

          // set a flag to confirm that we've collected the data-product types.
          thunkAPI.dispatch(setDPTRetrieved({ id: args.query.id, dptRetrieved: true }));
          //
        }
      }
      //
    } catch (e) {
      console.log(e);
    }
    //
  },
  //
); // getDataProductTypesInResults

//	------------------------------------------------------------
//
//	Extract a specific field value from the current row.
//
//	------------------------------------------------------------

export function getFieldValue(args: { fieldName: string; item: any }) {
  //
  // Check if the field exists directly in the item
  if (args.fieldName in args.item) {
    return args.item[args.fieldName];
  }

  // Handle case sensitivity issues
  const lowerFieldName = args.fieldName.toLowerCase();
  for (const key in args.item) {
    if (key.toLowerCase() === lowerFieldName) {
      return args.item[key];
    }
  }

  // Field not found
  return '';
  //
} // getFieldValue

//	------------------------------------------------------------
//
//	Get the current time in ISO format.
//
//	------------------------------------------------------------

export function getISOTime() {
  //
  const currentTime = new Date(Date.now());
  let isoTime: string = currentTime.toISOString();
  if (isoTime.length > 19) {
    isoTime = isoTime.slice(0, 19);
  }

  // return something.
  return isoTime;
  //
} // getISOTime

//	------------------------------------------------------------
//
//	Load the dataset from the TAP server.
//
//	------------------------------------------------------------

export const getTapData = createAsyncThunk(
  'search_catalog_caom/getTapData',
  async (args: { query: QueryParameters }, thunkAPI) => {
    //
    // fetch the state.
    const stateFetch: any = thunkAPI.getState();
    const state: SearchCatalogCAOMState = stateFetch.searchCatalogCAOM;

    // LOCAL STATE VARIABLES:
    // ------------------------------

    const sPageSize: number =
      state.pageSize.find((find) => find.id === args.query.id)?.pageSize ?? 10;

    // maintain the state for the selected rows on the results tabs.
    const sSelectedRows: SelectedRowsType[] = state.selectedItems.selectedRows;

    // LOCAL STATE VARIABLES FOR ALL TABS:
    // ------------------------------

    const sPageAllTabs: { id: string; page: number }[] = state.page;
    const sDPTFilterAllTabs: { id: string; dptFilter: string }[] = state.dptFilter;
    const sTotalAllTabs: { id: string; total: { dpt: string; total: number }[] }[] =
      state.total;

    // EXTRACT LOCAL STATE VARIABLES:
    // ------------------------------

    const sPage: number =
      sPageAllTabs.find((item) => item.id === args.query.id)?.page ?? 0;
    const sDPTFilter: string =
      sDPTFilterAllTabs.find((item) => item.id === args.query.id)?.dptFilter ?? '<all>';
    const sTotalAllDPT: { dpt: string; total: number }[] | undefined =
      sTotalAllTabs.find((item) => item.id === args.query.id)?.total ?? undefined;
    const sTotal: number =
      sTotalAllDPT?.find((item) => item.dpt === sDPTFilter)?.total ?? 0;

    thunkAPI.dispatch(setLoadingTapData({ id: args.query.id, loadingTapData: true }));

    let urlCommand: string = APIPrefix() + '/v1/data/get_data_caom';

    // Check if we have an ADQL query
    if (args.query.adqlQuery) {
      //
      // Clean and format the query
      const cleanQuery = args.query.adqlQuery.trim();

      // add the ADQL query.
      urlCommand = urlCommand + '?query=' + encodeURIComponent(cleanQuery);

      // add page and page size
      urlCommand = urlCommand + '&page=' + sPage + '&page_size=' + sPageSize;

      // add the total number of rows (if > 0).
      if (sTotal > 0) {
        urlCommand = urlCommand + '&limit_rows=' + sTotal;
      }

      try {
        //
        const apiResult = await fetch(urlCommand, { credentials: 'include' });
        const returnedJson = await apiResult.json();

        // Check for error status
        if (returnedJson.status === 'error') {
          console.error('API returned error:', returnedJson.message);
          thunkAPI.dispatch(
            setErrorMessage({
              id: args.query.id,
              errorMessage: returnedJson.message || 'Unknown error occurred',
            }),
          );
          thunkAPI.dispatch(
            setLoadingTapData({ id: args.query.id, loadingTapData: false }),
          );
          return;
        }

        // Clear any previous error
        thunkAPI.dispatch(setErrorMessage({ id: args.query.id, errorMessage: '' }));

        // get source list
        let sourceList: any = [];
        if (returnedJson.results !== undefined) {
          sourceList = returnedJson.results;
        }

        // Handle empty results
        if (Array.isArray(sourceList) && sourceList.length === 0) {
          thunkAPI.dispatch(setTapData({ id: args.query.id, tapData: [] }));
          thunkAPI.dispatch(setColumns({ id: args.query.id, columns: [] }));
          thunkAPI.dispatch(
            setLoadingTapData({ id: args.query.id, loadingTapData: false }),
          );
          thunkAPI.dispatch(
            setDisplayedPage({
              id: args.query.id,
              dptFilter: '',
              page: -1,
              pageSize: -1,
            }),
          );
          return;
        }

        // extract a list of columns
        let columns: string[] = [];

        // loop over all the rows
        if (Array.isArray(sourceList) === true) {
          sourceList.forEach((element: any) => {
            //
            // get a list of keys for this object
            let newColumns: string[] = Object.keys(element);

            // add these items if they don't already exist
            newColumns.forEach((column) => {
              if (columns.findIndex((element) => element === column) === -1)
                columns.push(column);
            });
            //
          });
        } else {
          //
          // get a list of keys for this object
          columns = Object.keys(sourceList);
        }

        // check if all the rows are selected
        const allSelected: boolean = checkSelectAll({
          sourceList: sourceList,
          selectedRows: sSelectedRows,
          columns: columns,
        });

        thunkAPI.dispatch(setTapData({ id: args.query.id, tapData: sourceList }));
        thunkAPI.dispatch(setColumns({ id: args.query.id, columns: columns }));
        thunkAPI.dispatch(
          setLoadingTapData({ id: args.query.id, loadingTapData: false }),
        );
        thunkAPI.dispatch(
          setDisplayedPage({
            id: args.query.id,
            dptFilter: sDPTFilter,
            page: sPage,
            pageSize: sPageSize,
          }),
        );
        thunkAPI.dispatch(setSelectAll({ id: args.query.id, selectAll: allSelected }));
        //
      } catch (e) {
        console.error('Error fetching data:', e);
        thunkAPI.dispatch(
          setErrorMessage({
            id: args.query.id,
            errorMessage: 'Failed to fetch data. Please try again.',
          }),
        );
        thunkAPI.dispatch(
          setLoadingTapData({ id: args.query.id, loadingTapData: false }),
        );
      }
      //
    } else {
      //
      // build the list of IDs as a comma-separated list.
      let requiredObsPublisherDid: string = '';
      if (args.query.obs_publisher_did.length > 0) {
        //
        for (let i = 0; i < args.query.obs_publisher_did.length; i++)
          requiredObsPublisherDid =
            requiredObsPublisherDid + "'" + args.query.obs_publisher_did[i] + "',";

        // remove the last comma.
        requiredObsPublisherDid = requiredObsPublisherDid.slice(0, -1);
        //
      }

      // add ra, dec, and fov.
      urlCommand = urlCommand + '?';
      if (
        args.query.ra !== undefined &&
        args.query.dec !== undefined &&
        args.query.fov !== undefined
      ) {
        urlCommand =
          urlCommand +
          'ra=' +
          args.query.ra +
          '&dec=' +
          args.query.dec +
          '&radius=' +
          args.query.fov +
          '&';
      }
      if (requiredObsPublisherDid !== '') {
        urlCommand =
          urlCommand +
          'obs_publisher_did=' +
          encodeURIComponent(requiredObsPublisherDid) +
          '&';
      }

      // Only add dataproduct_type filter if not "All"
      if (sDPTFilter !== '<all>') {
        urlCommand =
          urlCommand +
          'dataproduct_type=' +
          (sDPTFilter === '' ? '<empty>' : encodeURIComponent(sDPTFilter)) +
          '&';
      }

      // only filter on source name if this is NOT a position search.
      if (
        args.query.sourceName !== undefined &&
        (args.query.ra === undefined ||
          args.query.dec === undefined ||
          args.query.fov === undefined)
      ) {
        const sourceName: string | null = args.query.sourceName;
        urlCommand =
          urlCommand + 'source_name=' + encodeURIComponent(sourceName ?? '') + '&';
      }
      if (args.query.namespace !== undefined) {
        urlCommand =
          urlCommand + 'namespace=' + encodeURIComponent(args.query.namespace) + '&';
      }
      if (args.query.filename !== undefined) {
        urlCommand =
          urlCommand + 'filename=' + encodeURIComponent(args.query.filename) + '&';
      }
      if (args.query.collection !== undefined) {
        urlCommand =
          urlCommand + 'collection=' + encodeURIComponent(args.query.collection) + '&';
      }
      if (args.query.facility !== undefined) {
        urlCommand =
          urlCommand + 'facility=' + encodeURIComponent(args.query.facility) + '&';
      }
      if (args.query.instrument !== undefined) {
        urlCommand =
          urlCommand + 'instrument=' + encodeURIComponent(args.query.instrument) + '&';
      }

      // add page and page size.
      urlCommand = urlCommand + 'page=' + sPage + '&page_size=' + sPageSize;

      try {
        const apiResult = await fetch(urlCommand, { credentials: 'include' });
        const returnedJson = await apiResult.json();

        // Check for error status
        if (returnedJson.status === 'error') {
          console.error('API returned error:', returnedJson.message);
          thunkAPI.dispatch(
            setErrorMessage({
              id: args.query.id,
              errorMessage: returnedJson.message || 'Unknown error occurred',
            }),
          );
          thunkAPI.dispatch(
            setLoadingTapData({ id: args.query.id, loadingTapData: false }),
          );
          return;
        }

        // Clear any previous error
        thunkAPI.dispatch(setErrorMessage({ id: args.query.id, errorMessage: '' }));

        // get source list
        let sourceList: any = [];
        if (returnedJson.results !== undefined) {
          sourceList = returnedJson.results;
        }

        // Handle empty results
        if (Array.isArray(sourceList) && sourceList.length === 0) {
          thunkAPI.dispatch(setTapData({ id: args.query.id, tapData: [] }));
          thunkAPI.dispatch(setColumns({ id: args.query.id, columns: [] }));
          thunkAPI.dispatch(
            setLoadingTapData({ id: args.query.id, loadingTapData: false }),
          );
          thunkAPI.dispatch(
            setDisplayedPage({
              id: args.query.id,
              dptFilter: '',
              page: -1,
              pageSize: -1,
            }),
          );
          return;
        }

        // extract a list of columns
        let columns: string[] = [];

        // loop over all the rows
        if (Array.isArray(sourceList) === true) {
          sourceList.forEach((element: any) => {
            //
            // get a list of keys for this object
            let newColumns: string[] = Object.keys(element);

            // add these items if they don't already exist
            newColumns.forEach((column) => {
              if (columns.findIndex((element) => element === column) === -1)
                columns.push(column);
            });
            //
          });
        } else {
          //
          // get a list of keys for this object
          columns = Object.keys(sourceList);
          //
        }

        // check if all the rows are selected
        const allSelected: boolean = checkSelectAll({
          sourceList: sourceList,
          selectedRows: sSelectedRows,
          columns: columns,
        });

        thunkAPI.dispatch(setTapData({ id: args.query.id, tapData: sourceList }));
        thunkAPI.dispatch(setColumns({ id: args.query.id, columns: columns }));
        thunkAPI.dispatch(
          setLoadingTapData({ id: args.query.id, loadingTapData: false }),
        );
        thunkAPI.dispatch(
          setDisplayedPage({
            id: args.query.id,
            dptFilter: sDPTFilter,
            page: sPage,
            pageSize: sPageSize,
          }),
        );
        thunkAPI.dispatch(setSelectAll({ id: args.query.id, selectAll: allSelected }));
        //
      } catch (e) {
        console.error('Error fetching cone search data:', e);
        thunkAPI.dispatch(
          setErrorMessage({
            id: args.query.id,
            errorMessage: 'Failed to fetch data. Please try again.',
          }),
        );
        thunkAPI.dispatch(
          setLoadingTapData({ id: args.query.id, loadingTapData: false }),
        );
      }
    }
    //
  },
  //
); // getTapData

//	------------------------------------------------------------
//
//	update the available and moving locations of selected items
//	when the list of data-management jobs is updated.
//
//	------------------------------------------------------------

export function handleDMJobsUpdate(args: { dataManagementJobs: JobType[] }) {
  //
  // check if any files are on data-management jobs that have changed since the
  // file location was last checked.
  _checkIfRefreshNeeded({
    dataManagementJobs: args.dataManagementJobs,
  });

  // rebuild the list of files being staged, and the locations they are
  // being moved to.
  _buildMovingLocations({ dataManagementJobs: args.dataManagementJobs });
  //
} // handleDMJobsUpdate

//	------------------------------------------------------------
//
//	An asynchronous function that loads the collection data
//	from the back end.
//
//	------------------------------------------------------------

export const loadCollections = createAsyncThunk(
  'search_catalog_caom/loadCollections',
  async (args: { project?: string; lookUpCollection?: string }, thunkAPI) => {
    //
    if (args.project !== undefined && args.project !== '') {
      //
      let urlCommand: string =
        APIPrefix() + '/v1/get_data_collections?project=' + args.project;

      try {
        //
        const apiResult = await fetch(urlCommand, {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        // Return code 200 means the API has run.
        if (apiResult.status === 200) {
          //
          const returnedJson = await apiResult.json();

          // get collection list.
          let collectionList: {
            value: number;
            label: string;
          }[] = [];

          if (returnedJson.data_collections !== undefined) {
            collectionList = returnedJson.data_collections.map((item: any) => {
              return {
                value: item.collection_id,
                label: item.name,
              };
            });
          }

          // update the collection list options.
          thunkAPI.dispatch(setCollections(collectionList));

          // look for the collection passed as a parameter.
          if (args.lookUpCollection !== undefined && args.lookUpCollection !== '') {
            //
            const lookUpCollection: string = args.lookUpCollection;

            const index: number = collectionList.findIndex(
              (item) => item.label.toUpperCase() === lookUpCollection.toUpperCase(),
            );
            if (index > -1) {
              thunkAPI.dispatch(setExistingCollectionText(args.lookUpCollection));
              thunkAPI.dispatch(setExistingCollection(collectionList[index]));
            }
            //
          }
          //
        }
        //
      } catch (e) {}
      //
    }
    //
  },
  //
); // loadCollections

//	------------------------------------------------------------
//
//	load the list of data-product types from the back end.
//
//	------------------------------------------------------------

export const loadDataProductTypes = createAsyncThunk(
  'search_catalog_caom/loadDataProductTypes',
  async (_, thunkAPI) => {
    //
    let urlCommand: string = APIPrefix() + '/v1/data/get_product_types';

    try {
      const apiResult = await fetch(urlCommand, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (apiResult.status === 200) {
        const returnedJson = await apiResult.json();

        // get data-product types list.
        let dataProductTypesList: { dataproduct_type: string; num_records: number }[] =
          [];
        if (returnedJson.result !== undefined) {
          dataProductTypesList = returnedJson.result;
        }

        // update the state with the list of returned data-product types.
        thunkAPI.dispatch(setDataProductTypes(dataProductTypesList));
      }
    } catch (e) {}
    //
  },
  //
); // loadDataProductTypes

//	------------------------------------------------------------
//
//	load the list of queries from the back end.
//
//	------------------------------------------------------------

export const loadQueries = createAsyncThunk(
  'search_catalog_caom/loadQueries',
  async (args: { project_id: number }, thunkAPI) => {
    //
    let urlCommand: string =
      APIPrefix() + '/v1/fetch_data_query?project=' + args.project_id.toString();

    try {
      const apiResult = await fetch(urlCommand, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (apiResult.status === 200) {
        //
        const returnedJson = await apiResult.json();

        // get data-product types list.
        let queries: DataQuery[] = [];
        if (returnedJson.result !== undefined) {
          queries = returnedJson.result;
        }

        // update the state with the list of returned queries.
        thunkAPI.dispatch(setQueries(queries));
        //
      }
    } catch (e) {}
    //
  },
  //
); // loadQueries

//	------------------------------------------------------------
//
//	handle the click event for a context menu item.
//
//	------------------------------------------------------------

export function onContextMenuClickAddMetadata(args: { menuID: number; rowID: number[] }) {
  //
  const stateFetch: any = store.getState();
  const state: SearchCatalogCAOMState = stateFetch.searchCatalogCAOM;

  // if we've clicked on a metadata item on the meta data menu
  // then display that item.
  if (args.menuID === CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.METADATA_LIST) {
    //
    if (args.rowID[CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.METADATA_LIST] === 0) {
      if (state.filter.namespaceDisplayed === false) {
        store.dispatch(setNamespaceValue(''));
      }
      store.dispatch(setNamespaceDisplayed(true));
    }
    if (args.rowID[CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.METADATA_LIST] === 1) {
      if (state.filter.filenameDisplayed === false) {
        store.dispatch(setFilenameValue(''));
      }
      store.dispatch(setFilenameDisplayed(true));
    }
    if (args.rowID[CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.METADATA_LIST] === 2) {
      if (state.filter.collectionDisplayed === false) {
        store.dispatch(setCollectionValue(''));
      }
      store.dispatch(setCollectionDisplayed(true));
    }
    if (args.rowID[CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.METADATA_LIST] === 3) {
      if (state.filter.facilityDisplayed === false) {
        store.dispatch(setFacilityValue(''));
      }
      store.dispatch(setFacilityDisplayed(true));
    }
    if (args.rowID[CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.METADATA_LIST] === 4) {
      if (state.filter.instrumentDisplayed === false) {
        store.dispatch(setInstrumentValue(''));
      }
      store.dispatch(setInstrumentDisplayed(true));
    }
    //
  }
  //
} // onContextMenuClickAddMetadata

//	------------------------------------------------------------
//
//	handle the click event for a context menu item by launching
//	the move-data process.
//
//	------------------------------------------------------------

export function onContextMenuClickStageData(args: { menuID: number; rowID: number[] }) {
  //
  const stateFetch: any = store.getState();
  const state: SearchCatalogCAOMState = stateFetch.searchCatalogCAOM;
  const stateData: DataState = stateFetch.data;
  const contextMenuState: ContextMenuState = stateFetch.contextMenu;

  // if we've clicked to remove item(s) from the selected list.
  if (args.menuID === CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.MAIN) {
    //
    const clickedItem: ContextMenuItem =
      contextMenuState.contextMenuDefinition[
        CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS
      ][args.menuID].items[args.rowID[args.menuID]];

    // just selected items? build a list of the selected items, and update the state.
    if (clickedItem.id === 'mnu-main-unselectitem') {
      //
      let removeItems: SelectedRowsType[] = state.selectedItems.selectedRows.filter(
        (filter) =>
          state.selectedItems.selectedFiles.findIndex(
            (find) =>
              find.namespace === filter.namespace && find.filename === filter.filename,
          ) > -1,
      );
      let ticked: boolean[] = Array(removeItems.length).fill(false);
      store.dispatch(
        rowCheckedHandler({
          rows: removeItems,
          ticked: ticked,
        }),
      );
      //
    }

    // all items? build a list of all items, and update the state.
    if (clickedItem.id === 'mnu-main-unselectall') {
      //
      let ticked: boolean[] = Array(state.selectedItems.selectedRows.length).fill(false);
      store.dispatch(
        rowCheckedHandler({
          rows: state.selectedItems.selectedRows,
          ticked: ticked,
        }),
      );
      //
    }
    //
  }

  // if we've clicked on a storage area on the storages menu
  // then launch the move-data process.
  if (args.menuID === CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.STORAGES) {
    //
    // check which item was selected from the main menu.
    const itemMain: ContextMenuItem =
      contextMenuState.contextMenuDefinition[
        CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS
      ][CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.MAIN].items[
        contextMenuState.contextRow[CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS][
          CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.MAIN
        ]
      ];
    const moveAll: boolean = itemMain.id === 'mnu-main-moveallto';

    // build a list of selected files.
    const filesToMove: DataItemType[] = [];

    // add either the selected items, or all the items.
    if (moveAll === true) {
      //
      for (let idx = 0; idx < state.selectedItems.selectedRows.length; idx++) {
        //
        filesToMove.push({
          namespace: state.selectedItems.selectedRows[idx].namespace,
          filename: state.selectedItems.selectedRows[idx].filename,
          did_type: 'FILE',
          bytes: 0,
        });
      }
      //
    } else {
      //
      for (let idx = 0; idx < state.selectedItems.selectedFiles.length; idx++) {
        //
        filesToMove.push({
          namespace: state.selectedItems.selectedFiles[idx].namespace,
          filename: state.selectedItems.selectedFiles[idx].filename,
          did_type: 'FILE',
          bytes: 0,
        });
      }
      //
    }

    // moving data to a storage location.
    const moveDataToStorage: MoveDataToStorage = {
      toStorageAreaUUID:
        stateData.storageAreas[
          args.rowID[CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.SITES]
        ].storage_areas[args.rowID[CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.STORAGES]]
          .storage_id,
      toSiteUUID:
        stateData.storageAreas[
          args.rowID[CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.SITES]
        ].site.site_uuid ?? '',
      toStorageIdentifier:
        stateData.storageAreas[
          args.rowID[CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.SITES]
        ].storage_areas[args.rowID[CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.STORAGES]]
          .name,
      lifetime: 3600,
      filesToMove: filesToMove,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.MOVE_DATA_TO_STORAGE,
      parameters: moveDataToStorage,
    };
    store.dispatch(handleEvent({ event: newEvent }));

    store.dispatch(rG_setSubmittingJob(true));
    //
  }
  //
} // onContextMenuClickStageData
